import React, { useEffect, useMemo, useState } from "react";
import { Hooks, Util } from "@xt/xtbase";
import { observer } from "mobx-react-lite";
import { useStore } from "@/models";
import XtButton from "@/components/App/XtButton";
import Swal from "@/utils/swal";
import styles from "./recommend.module.less";
import { getRecommendPairs } from "./recommendPairs";
import { publicSvgUrl } from "@/utils/publicSvgUrl";

const { useTranslation } = Hooks;
const { showRate } = Util;

const MarketListRecommend = ({
  resData,
  wsData,
  preferFiat,
  activeFiatCodes,
  activeFiatCode,
  /** 首页 Favorites 子 Tab：只推荐当前类型下的交易对 */
  subTab = "",
  onGoToCryptoTab,
}) => {
  const t = useTranslation();
  const { market, user } = useStore();
  const [selected, setSelected] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const recommendPairs = useMemo(
    () =>
      getRecommendPairs({
        marketConfig: market.marketConfig,
        resData,
        wsData,
        activeFiatCodes,
        activeFiatCode,
        subTab,
        limit: 6,
      }),
    [market.marketConfig, resData, wsData, activeFiatCodes, activeFiatCode, subTab]
  );

  /** 推荐 6 条变化时默认全部勾选 */
  const recommendSymbolsKey = useMemo(
    () => recommendPairs.map((p) => p.s).join("|"),
    [recommendPairs]
  );

  useEffect(() => {
    if (!recommendSymbolsKey) {
      setSelected([]);
      return;
    }
    setSelected(recommendSymbolsKey.split("|").filter(Boolean));
  }, [recommendSymbolsKey]);

  const toggleSelect = (symbol) => {
    setSelected((prev) => (prev.includes(symbol) ? prev.filter((s) => s !== symbol) : [...prev, symbol]));
  };

  const onPrimaryClick = async () => {
    if (selected.length === 0) {
      if (onGoToCryptoTab) onGoToCryptoTab(preferFiat);
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    try {
      const current = Array.isArray(user.userMarket) ? user.userMarket : [];
      const merged = Array.from(new Set([...current, ...selected]));
      await user.setUserMarket(merged);
      setSelected([]);
    } catch (e) {
      Swal.fadeError(typeof e === "string" ? e : (e && e.message) || "error");
    } finally {
      setSubmitting(false);
    }
  };

  const getRate = (item) => {
    const v = +(item.cr || item.r) || 0;
    return { text: showRate(v * 100), isUp: v > 0, isZero: v === 0 };
  };

  if (!recommendPairs.length) {
    return null;
  }

  return (
    <div className={styles.recommend}>
      <div className={styles.grid}>
        {recommendPairs.map((item) => {
          const [base, quote] = item.s.split("_");
          const isSel = selected.includes(item.s);
          const rate = getRate(item);
          return (
            <div
              key={`rec-${item.s}`}
              className={`${styles.card} ${isSel ? styles.cardSelected : ""}`}
              onClick={() => toggleSelect(item.s)}
              role="button"
            >
              <div className={styles.cardBody}>
                <div className={styles.cardLeft}>
                  <div className={styles.symbolRow}>
                    <span className={styles.base}>{(base || "").toUpperCase()}</span>
                    <span className={styles.quote}>/ {(quote || "").toUpperCase()}</span>
                  </div>
                  <div
                    className={`${styles.rate} ${rate.isZero ? "" : rate.isUp ? styles.up : styles.down}`}
                  >
                    {rate.text}
                  </div>
                </div>
                <div
                  className={styles.checkbox}
                  style={{
                    backgroundImage: `url("${publicSvgUrl(
                      isSel ? "fav-recommend-checked.svg" : "fav-recommend-subtract.svg"
                    )}")`,
                    backgroundSize: isSel ? "14px 14px" : "12px 12px",
                  }}
                  aria-hidden="true"
                />
              </div>
            </div>
          );
        })}
      </div>
      <XtButton
        className={styles.primaryBtn}
        disabled={submitting}
        onClick={onPrimaryClick}
      >
        {t("market.addToFavorites")}
      </XtButton>
    </div>
  );
};

export default observer(MarketListRecommend);
